interface BrowserCmsCacheRecord<TPayload> {
  version: number
  expiresAt: number
  payload: TPayload
}

interface ReadBrowserCmsCacheOptions<TPayload> {
  key: string
  version: number
  validate?: (value: unknown) => value is TPayload
  now?: number
}

interface WriteBrowserCmsCacheOptions<TPayload> {
  key: string
  version: number
  ttlMs: number
  payload: TPayload
  now?: number
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object'
}

const isBrowserStorageAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

const removeBrowserCmsCache = (key: string): void => {
  if (!isBrowserStorageAvailable()) {
    return
  }

  window.localStorage.removeItem(key)
}

const isBrowserCmsCacheRecord = <TPayload>(
  value: unknown,
  validatePayload?: (payload: unknown) => payload is TPayload,
): value is BrowserCmsCacheRecord<TPayload> => {
  if (!isRecord(value)) {
    return false
  }

  if (typeof value.version !== 'number' || typeof value.expiresAt !== 'number') {
    return false
  }

  if (!validatePayload) {
    return true
  }

  return validatePayload(value.payload)
}

export const readBrowserCmsCache = <TPayload>(
  options: ReadBrowserCmsCacheOptions<TPayload>,
): TPayload | null => {
  if (!isBrowserStorageAvailable()) {
    return null
  }

  const rawCache = window.localStorage.getItem(options.key)

  if (!rawCache) {
    return null
  }

  try {
    const parsedCache = JSON.parse(rawCache) as unknown

    if (!isBrowserCmsCacheRecord(parsedCache, options.validate)) {
      removeBrowserCmsCache(options.key)
      return null
    }

    const now = options.now || Date.now()

    if (parsedCache.version !== options.version || parsedCache.expiresAt <= now) {
      removeBrowserCmsCache(options.key)
      return null
    }

    return parsedCache.payload
  } catch {
    removeBrowserCmsCache(options.key)
    return null
  }
}

export const writeBrowserCmsCache = <TPayload>(
  options: WriteBrowserCmsCacheOptions<TPayload>,
): void => {
  if (!isBrowserStorageAvailable()) {
    return
  }

  const now = options.now || Date.now()

  const cacheRecord: BrowserCmsCacheRecord<TPayload> = {
    version: options.version,
    expiresAt: now + options.ttlMs,
    payload: options.payload,
  }

  try {
    window.localStorage.setItem(options.key, JSON.stringify(cacheRecord))
  } catch {
    removeBrowserCmsCache(options.key)
  }
}
